import {
  addDaysToKey,
  addRomeDays,
  apiDateMs,
  type DayKey,
  diffLocalDays,
  diffRomeDays,
  dmyToDayKey,
  dayKeyToDmy,
  isDayKeyBetween,
  romeDayKeyOf,
  romeTodayKey,
  romeWallClock,
  toDayKey,
} from '@/lib/rome-date';

import {
  Village,
  VillageDeal,
  VillageFutureBooking,
  VillagePrice,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';

type Bedrooms = {
  single: number;
  double: number;
  flexible: number;
  twin: number;
  triple: number;
  quadruple: number;
};

type SelectedDates = {
  start: Date | null;
  end: Date | null;
};

type Props = {
  selectedDates?: SelectedDates;
  selectedPeriod?: VillagePricePeriod;
  priceVariant?: VillagePriceVariants;
  village?: Village;
};

// Long stay promos (Multiple Week Promo) are the only ones with 14+ min nights.
// They count the nights charged by the weekly rule; the others count the real stay.
const LONG_STAY_MIN_NIGHTS = 14;

// Instants (epoch ms) in Rome; the end is the day before check-out
type DisabledDate = {
  timestampStart: number;
  timestampEnd: number;
  type: string;
};

export default function useVillage({
  selectedDates,
  selectedPeriod,
  priceVariant,
  village,
}: Props) {
  const allPropsAreDefined =
    !!selectedDates && !!selectedPeriod && !!priceVariant && !!village;

  // An API date that may be missing counts as now (bookings, deal visibility)
  function apiDateMsOrNow(value?: Date | string | null) {
    return value ? apiDateMs(value) : Date.now();
  }

  function romeDayKeyOrToday(value?: string | null) {
    return value ? romeDayKeyOf(value) : romeTodayKey();
  }

  function countNights(dates: SelectedDates) {
    return diffLocalDays(dates.end, dates.start);
  }

  function getMinNights({
    selectedDates,
    village,
    nightsCount,
  }: {
    selectedDates: SelectedDates;
    village: Village;
    nightsCount: number;
  }) {
    const minNights: number[] = [];
    const endDay = toDayKey(selectedDates.end);
    const dayBeforeEndDate = endDay && addDaysToKey(endDay, -1);
    const startDate = toDayKey(selectedDates.start);

    if (nightsCount > 0) {
      village.prices.forEach(({ period }) => {
        const periodStart = romeDayKeyOf(period.startDate);
        const periodEnd = romeDayKeyOf(period.endDate);
        if (
          isDayKeyBetween(startDate, periodStart, periodEnd) ||
          isDayKeyBetween(dayBeforeEndDate, periodStart, periodEnd)
        ) {
          minNights.push(period.minNights);
        }
      });
    }

    return minNights.length > 0 ? Math.max(...minNights) : 1;
  }

  function isWeeklyBooking({
    selectedDates,
    village,
    nightsCount,
  }: {
    selectedDates: SelectedDates;
    village: Village;
    nightsCount: number;
  }) {
    let isWeeklyBooking = false;
    const endDay = toDayKey(selectedDates.end);
    const dayBeforeEndDate = endDay && addDaysToKey(endDay, -1);

    if (nightsCount > 0) {
      village.prices.forEach(({ period }) => {
        const { startDate, endDate } = period;
        if (
          isDayKeyBetween(
            dayBeforeEndDate,
            romeDayKeyOf(startDate),
            romeDayKeyOf(endDate),
          )
        ) {
          if (period.weeklyBooking) {
            isWeeklyBooking = true;
          }
        }
      });
    }
    return isWeeklyBooking;
  }

  function getNightsToCharge(
    isWeeklyBooking: boolean,
    daysCount: number,
    minNights: number,
    type?: 'optioned' | string,
  ) {
    if (type === 'optioned') {
      return daysCount;
    }

    if (isWeeklyBooking) {
      return Math.ceil(daysCount / 7) * 7;
    }

    return Math.max(daysCount, minNights);
  }

  function getDatePickerLastAvailableDate(
    priceVariants: VillagePriceVariants[],
  ) {
    const currentYear = romeWallClock().year;

    const availableYears = priceVariants
      .filter(
        ({ enabled, villaPricing }) =>
          villaPricing.year >= currentYear && enabled && villaPricing.enabled,
      )
      .map(({ villaPricing }) => villaPricing.year);

    const lastYear = Math.max(...availableYears);

    const lastAvailablePriceVariant = priceVariants.find(
      ({ villaPricing }) => villaPricing.year === lastYear,
    );

    if (lastAvailablePriceVariant) {
      const { villaPricing } = lastAvailablePriceVariant;

      const lastPeriod = villaPricing.pricePeriods.reduce((latest, current) => {
        return apiDateMsOrNow(current.endDate) >
          (latest.endDate ? apiDateMs(latest.endDate) : NaN)
          ? current
          : latest;
      }, villaPricing.pricePeriods[0]);

      return lastPeriod.endDate;
    }
  }

  function smartBedroomSelect({
    countRooms,
    count,
  }: {
    countRooms: Bedrooms;
    count: number;
  }) {
    let leftCount = count;
    const totalSleeps = { value: 0 };
    const selectedQuantities: { [key: string]: number } = {
      single: 0,
      double: 0,
      flexible: 0,
      twin: 0,
      triple: 0,
      quadruple: 0,
    };

    const bedrooms: { [key: string]: { maxQuantity: number; sleeps: number } } =
      {
        single: { maxQuantity: countRooms.single, sleeps: 1 },
        double: { maxQuantity: countRooms.double, sleeps: 2 },
        twin: { maxQuantity: countRooms.twin, sleeps: 2 },
        triple: { maxQuantity: countRooms.triple, sleeps: 3 },
        quadruple: { maxQuantity: countRooms.quadruple, sleeps: 4 },
        flexible: { maxQuantity: countRooms.flexible, sleeps: 0 },
      };

    // In this way we fill first the biggest rooms
    const roomKeys = Object.keys(bedrooms).reverse();

    roomKeys.forEach((key) => {
      if (bedrooms[key].maxQuantity > 0 && leftCount > 0) {
        if (leftCount > bedrooms[key].maxQuantity) {
          selectedQuantities[key] = bedrooms[key].maxQuantity;
          leftCount -= bedrooms[key].maxQuantity;
        } else {
          selectedQuantities[key] = leftCount;
          leftCount = 0;
        }
        totalSleeps.value += selectedQuantities[key] * bedrooms[key].sleeps;
      }
    });

    if (selectedQuantities.flexible > 0) {
      selectedQuantities.double += selectedQuantities.flexible;
      selectedQuantities.flexible = 0;
    }

    selectedQuantities.double += leftCount; // The flexible rooms will be marked as double

    const filteredSelectedQuantities = Object.keys(selectedQuantities)
      .filter((key) => selectedQuantities[key] > 0)
      .reduce<{ [key: string]: number }>((obj, key) => {
        obj[key] = selectedQuantities[key];
        return obj;
      }, {});

    return filteredSelectedQuantities;
  }

  function getDailyVillagePrices(
    village: Village,
    selectedDates: SelectedDates,
    totalBedrooms: number,
    totalGuests: number,
    type?: string,
  ) {
    const dailyPrices = [];
    const nightsCount = countNights(selectedDates);
    const minNights = getMinNights({ selectedDates, village, nightsCount });
    const weeklyBooking =
      type === 'optioned'
        ? false
        : isWeeklyBooking({
            selectedDates,
            village,
            nightsCount,
          });

    let curDate = toDayKey(selectedDates.start);

    const nightsToCharge = getNightsToCharge(
      weeklyBooking,
      nightsCount,
      minNights,
      type,
    );

    // A missing night count charges no nights
    const chargeEndDate =
      curDate && (addDaysToKey(curDate, nightsToCharge) ?? curDate);

    while (curDate && chargeEndDate && curDate < chargeEndDate) {
      const dailyPrice: { date: string; price: null | number } = {
        date: dayKeyToDmy(curDate),
        price: null,
      };

      const villagePrice = findVillagePrice(
        village,
        curDate,
        totalBedrooms,
        totalGuests,
      );

      if (villagePrice) {
        if (
          dailyPrice.price &&
          parseFloat(villagePrice.originalPrice) < dailyPrice.price
        ) {
          dailyPrice.price = parseInt(villagePrice.originalPrice) / 7;
        }
        if (dailyPrice.price == null) {
          dailyPrice.price = parseInt(villagePrice.originalPrice) / 7;
        }
      }

      dailyPrices.push(dailyPrice);
      curDate = addDaysToKey(curDate, 1);
    }

    return dailyPrices;
  }

  function getPricePeriod({
    priceVariant,
    selectedDates,
  }: {
    priceVariant: VillagePriceVariants;
    selectedDates: SelectedDates;
  }) {
    const period = priceVariant.villaPricing.pricePeriods.find((period) => {
      if (selectedDates.start && selectedDates.end) {
        return isDayKeyBetween(
          toDayKey(selectedDates.start),
          romeDayKeyOf(period.startDate),
          romeDayKeyOf(period.endDate),
        );
      }
    });
    return period;
  }

  function getPriceVariant({
    priceVariants,
    totalBedrooms,
    totalGuests,
    nextYear,
  }: {
    priceVariants: VillagePriceVariants[];
    totalBedrooms: number;
    totalGuests: number;
    nextYear?: boolean;
  }): VillagePriceVariants | null {
    let currentYear = romeWallClock().year;

    if (nextYear) {
      currentYear += 1;
    }

    const currentYearPricevariants = priceVariants.filter(
      (variant) => variant.villaPricing.year === currentYear && variant.enabled,
    );

    let curPriceVariant: VillagePriceVariants | null = null;

    currentYearPricevariants.forEach((variant) => {
      if (
        totalBedrooms <= variant.bedrooms &&
        totalGuests <= variant.sleeps &&
        (curPriceVariant === null || variant.sleeps < curPriceVariant.sleeps)
      ) {
        curPriceVariant = variant;
      }
    });

    return curPriceVariant;
  }

  function findVillagePrice(
    village: Village,
    date: DayKey,
    totalBedrooms: number,
    totalGuests: number,
  ) {
    const pricesForDate = village.prices?.filter((price) => {
      const priceVariant = price.variant;

      const isBetween = isDayKeyBetween(
        date,
        toDayKey(price.period.startDate),
        toDayKey(price.period.endDate),
      );

      const isMatchingVariant =
        totalBedrooms <= priceVariant.bedrooms &&
        totalGuests <= priceVariant.sleeps;

      return isBetween && isMatchingVariant;
    });

    if (!pricesForDate || pricesForDate.length === 0) {
      return null;
    }

    const bestPrice = pricesForDate.reduce((prev, current) => {
      const prevPrice = parseFloat(prev.originalPrice);
      const currentPrice = parseFloat(current.originalPrice);

      return currentPrice < prevPrice ? current : prev;
    });

    return bestPrice || null;
  }

  function getOriginalPrice(
    village: Village,
    selectedDates: SelectedDates,
    totalBedrooms: number,
    totalGuests: number,
    type?: string,
  ) {
    let villaPrice = 0;
    let invalidPrice = false;

    const dailyPrices = getDailyVillagePrices(
      village,
      selectedDates,
      totalBedrooms,
      totalGuests,
      type,
    );

    dailyPrices.forEach((dailyPrice) => {
      if (dailyPrice.price === null) {
        invalidPrice = true;
      } else {
        villaPrice += dailyPrice.price;
      }
    });

    if (invalidPrice) {
      return {
        originalPrice: 0,
        rawPrice: 0,
        dailyPrices,
      };
    }

    return {
      originalPrice: Math.round(villaPrice),
      // Unrounded sum of the nightly prices. The backend subtracts the deal discount
      // from this raw value and only rounds afterwards; rounding here first makes the
      // front total drift by 1 EUR. Keep both so each consumer picks the right one.
      rawPrice: villaPrice,
      dailyPrices,
    };
  }

  function getVillagePrice(
    // Raw nightly sum, not the rounded originalPrice. Mirror of
    // VillaBookingHandler::getVillaBookingPrice in vt-split: the discount is applied
    // to the raw sum and the result is rounded only at the end. Rounding before the
    // discount drifts the front total 1 EUR away from the backend order.
    rawPrice: number,
    villageDeals: VillageDeal[],
    dailyPrices: {
      date: string;
      price: number | null;
    }[],
    // Real check-out chosen by the customer, before the weekly rule rounds the stay up
    checkOut: SelectedDates['end'],
  ) {
    const villaPrice: { price: number; deal: VillageDeal | null } = {
      price: Math.round(rawPrice),
      deal: null,
    };
    const today = romeTodayKey();

    const visebleDeals = villageDeals.filter(({ enabled, deal }) => {
      const isVisible = isDayKeyBetween(
        today,
        romeDayKeyOrToday(deal.visibleFrom),
        romeDayKeyOrToday(deal.visibleTo),
      );

      return enabled && isVisible;
    });

    let bestVillaPrice = Number.MAX_SAFE_INTEGER;
    // Picker date counted by its own day; an unreadable one counts no real night
    const checkOutDay = checkOut ? toDayKey(checkOut) : null;

    visebleDeals.forEach(({ deal, enabled, document }) => {
      let totalsToBeDiscounted = 0;
      let discountedNights = 0;
      let realDiscountedNights = 0;
      let curVillaPrice = rawPrice;

      const dealStartsAt = toDayKey(deal.startsAt);
      const dealEndsAt = toDayKey(deal.endsAt);

      dailyPrices.forEach((dailyPrice) => {
        const date = dmyToDayKey(dailyPrice.date);

        if (isDayKeyBetween(date, dealStartsAt, dealEndsAt)) {
          totalsToBeDiscounted += dailyPrice.price ?? 0;
          discountedNights++;

          // Nights before the real check-out; the weekly rule may charge more
          if (!checkOut || (!!checkOutDay && date! < checkOutDay)) {
            realDiscountedNights++;
          }
        }
      });

      // dailyPrices run until the charged end (weekly rule). Long stay promos count those
      // charged nights; the others need the real stay, like the vt-split computePrice.
      const isLongStay = deal.minNights >= LONG_STAY_MIN_NIGHTS;
      const dealCanBeAppliedAndReturned =
        discountedNights >= deal.minNights &&
        (isLongStay || realDiscountedNights >= deal.minNights);

      if (deal !== null && dealCanBeAppliedAndReturned) {
        if (deal.type === 'fixed') {
          curVillaPrice -= parseFloat(deal.value);
        } else {
          curVillaPrice -= totalsToBeDiscounted * (parseInt(deal.value) / 100);
        }
      }

      if (curVillaPrice < bestVillaPrice) {
        bestVillaPrice = curVillaPrice;
        villaPrice.price = Math.round(curVillaPrice);
        if (discountedNights > 0 && dealCanBeAppliedAndReturned) {
          villaPrice.deal = { enabled, deal, document };
        }
      }
    });

    return villaPrice;
  }

  function checkIfPeriodHasMinNights(
    dates: SelectedDates,
    period: VillagePricePeriod,
  ) {
    const periodMinNights = period.minNights;
    const nightsCount = countNights(dates);

    return nightsCount >= periodMinNights;
  }

  function checkIfCanCheckIn(
    date: Date,
    priceVariants: VillagePriceVariants[],
  ) {
    let canCheckIn = true;
    let checkInDays: string[] = [];
    let weekDay = new Date(date).getDay() - 1;
    const isSunday = weekDay === -1;

    if (isSunday) {
      weekDay = 6;
    }

    const weekDayString = weekDay.toString();

    const findedPeriod = findPeriodInVariants(priceVariants, date);

    if (findedPeriod) {
      checkInDays = findedPeriod.checkInDays;
      canCheckIn = checkInDays.some((checkIn) => checkIn === weekDayString);
    }

    return {
      canCheckIn,
      checkInDays,
    };
  }

  function getUnavailableDates(futureBookings: VillageFutureBooking[]) {
    const currentDate = new Date();

    const unavailableDates: { busy: DisabledDate[]; optioned: DisabledDate[] } =
      {
        busy: [],
        optioned: [],
      };

    let previousDate: Omit<DisabledDate, 'type'> | null = null;

    futureBookings?.map((booking) => {
      if (
        !['booked', 'not_available', 'cart', 'optioned'].includes(booking.type)
      ) {
        return;
      }

      const currentDate = {
        timestampStart: apiDateMsOrNow(booking.timestampStart),
        timestampEnd: addRomeDays(apiDateMsOrNow(booking.timestampEnd), -1),
      };

      if (booking.type === 'cart') {
        currentDate.timestampStart = addRomeDays(
          apiDateMsOrNow(booking.timestampStart),
          -1,
        );
      }

      if (previousDate) {
        const diff = diffRomeDays(
          currentDate.timestampStart,
          previousDate.timestampEnd,
        );
        // an unavailability is added between one booking and another
        // if there are not at least 4 days
        if (diff >= 1 && diff <= 4) {
          unavailableDates.busy.push({
            timestampStart: previousDate.timestampEnd,
            timestampEnd: currentDate.timestampStart,
            type: 'busy',
          });
        }
      }

      previousDate = { ...currentDate };
      if (booking.type === 'booked' || booking.type === 'not_available') {
        unavailableDates.busy.push({ ...currentDate, type: 'busy' });
      }

      if (booking.type === 'cart' || booking.type === 'optioned') {
        unavailableDates.optioned.push({ ...currentDate, type: 'optioned' });
      }
    });
    return unavailableDates;
  }

  function findPeriodInVariants(
    priceVariants: VillagePriceVariants[],
    date: Date,
  ) {
    let period: VillagePricePeriod | undefined;

    const formatedDate = new Date(date);
    const currentPriceVariants = priceVariants.filter(
      (variant) => variant.villaPricing.year === formatedDate.getFullYear(),
    );

    currentPriceVariants.forEach((variant) => {
      const findedPeriod = variant.villaPricing.pricePeriods.find((period) => {
        const startDate = new Date(period.startDate);
        const endDate = new Date(period.endDate);
        const startPeriodTime =
          startDate.getTime() + startDate.getTimezoneOffset() * 60000;
        const endPeriodTime =
          endDate.getTime() + endDate.getTimezoneOffset() * 60000;

        return (
          formatedDate.getTime() >= startPeriodTime &&
          formatedDate.getTime() <= endPeriodTime
        );
      });

      if (findedPeriod) {
        period = findedPeriod;
      }
    });

    if (period) {
      return period;
    }
  }

  function getSecurityDeposit(
    priceVariants: VillagePriceVariants[],
    totalBedrooms: number,
    totalGuests: number,
  ) {
    const currentDate = new Date();

    const currentYearPriceVariantsSecurityDeposits = priceVariants.filter(
      (variant) => {
        if (variant.villaPricing.year === currentDate.getFullYear()) {
          return variant;
        }
      },
    );

    let curPriceVariant: VillagePriceVariants | null = null;

    currentYearPriceVariantsSecurityDeposits.forEach((variant) => {
      if (
        totalBedrooms <= variant.bedrooms &&
        totalGuests <= variant.sleeps &&
        (curPriceVariant === null || variant.sleeps < curPriceVariant.sleeps)
      ) {
        curPriceVariant = variant;
      }
    });

    const securityDeposit =
      Number(
        (curPriceVariant as VillagePriceVariants | null)?.securityDeposit,
      ) ?? 0;

    return securityDeposit;
  }

  return {
    checkIfPeriodHasMinNights,
    checkIfCanCheckIn,
    getOriginalPrice,
    countNights,
    getNightsToCharge,
    getUnavailableDates,
    isWeeklyBooking,
    getVillagePrice,
    getPriceVariant,
    getPricePeriod,
    smartBedroomSelect,
    getDatePickerLastAvailableDate,
    getMinNights,
    getSecurityDeposit,
  };
}
